/**
 * 既存来場者の利用規約再同意状態フック
 * DB内で判定した有効規約への同意要否をホームのブロッキング表示へ接続します
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  visitorAcceptActiveTerms,
  visitorGetTermsConsentStatus,
  VISITOR_TERMS_RECONSENT_VERSION_OUTDATED_CODE,
} from '../services/visitorAuthService';
import { logError } from '../../../shared/utils/logger';

/** 再同意状態 */
const TERMS_RECONSENT_STATUS = Object.freeze({
  IDLE: 'idle',
  CHECKING: 'checking',
  SATISFIED: 'satisfied',
  REQUIRED: 'required',
  SUBMITTING: 'submitting',
  ERROR: 'error',
});

/** 再同意処理状態の型 */
type TermsReconsentStatus =
  | 'idle'
  | 'checking'
  | 'satisfied'
  | 'required'
  | 'submitting'
  | 'error';

/**
 * 既存来場者の利用規約再同意状態を管理する
 * @param {string} sessionToken - 来場者セッショントークン
 * @returns {{
 * terms: { termsId: string, version: string, title: string, content: string, effectiveAt: string } | null,
 * isBlocking: boolean,
 * isChecking: boolean,
 * isSubmitting: boolean,
 * isTermsAccepted: boolean,
 * errorMessage: string,
 * hasLoadError: boolean,
 * handleToggleTermsAccepted: () => void,
 * handleAcceptTerms: () => Promise<void>,
 * handleRetryConsentStatus: () => Promise<void>,
 * }} 再同意状態と操作
 */
const useVisitorTermsReconsent = (sessionToken) => {
  /** 再同意処理状態 */
  const [status, setStatus] = useState<TermsReconsentStatus>(
    sessionToken ? TERMS_RECONSENT_STATUS.CHECKING : TERMS_RECONSENT_STATUS.IDLE,
  );
  /** DBが返した現在有効な利用規約 */
  const [terms, setTerms] = useState(null);
  /** 明示同意チェック状態 */
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  /** 取得・送信エラーメッセージ */
  const [errorMessage, setErrorMessage] = useState('');
  /** 古い非同期応答を破棄するための要求ID */
  const requestIdRef = useRef(0);

  /**
   * DB内で有効規約への同意要否を再取得する
   * @returns {Promise<void>}
   */
  const loadConsentStatus = useCallback(async () => {
    /** 今回の要求ID */
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsTermsAccepted(false);
    setErrorMessage('');
    setTerms(null);

    if (!sessionToken) {
      setStatus(TERMS_RECONSENT_STATUS.IDLE);
      return;
    }

    setStatus(TERMS_RECONSENT_STATUS.CHECKING);

    try {
      /** DBで判定した利用規約同意状況 */
      const consentStatus = await visitorGetTermsConsentStatus(sessionToken);

      if (requestIdRef.current !== requestId) {
        return;
      }

      setTerms(consentStatus.terms);
      setStatus(
        consentStatus.requiresAcceptance
          ? TERMS_RECONSENT_STATUS.REQUIRED
          : TERMS_RECONSENT_STATUS.SATISFIED,
      );
    } catch (error) {
      logError('利用規約の再同意要否を確認できませんでした。', {
        errorCode: error?.code,
        errorMessage: error instanceof Error ? error.message : String(error),
      });

      if (requestIdRef.current !== requestId) {
        return;
      }

      setStatus(TERMS_RECONSENT_STATUS.ERROR);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : '利用規約の同意状況を確認できませんでした。',
      );
    }
  }, [sessionToken]);

  useEffect(() => {
    /**
     * セッション確定後に再同意要否を確認する
     * @returns {void}
     */
    const initializeConsentStatus = () => {
      void loadConsentStatus();
    };

    initializeConsentStatus();

    return () => {
      requestIdRef.current += 1;
    };
  }, [loadConsentStatus]);

  /**
   * 利用規約への明示同意チェックを切り替える
   * @returns {void}
   */
  const handleToggleTermsAccepted = useCallback(() => {
    if (status !== TERMS_RECONSENT_STATUS.REQUIRED) {
      return;
    }

    setIsTermsAccepted(!isTermsAccepted);
  }, [isTermsAccepted, status]);

  /**
   * 表示中の有効規約への再同意をDBへ記録する
   * @returns {Promise<void>}
   */
  const handleAcceptTerms = useCallback(async () => {
    if (
      status !== TERMS_RECONSENT_STATUS.REQUIRED
      || !terms
      || !isTermsAccepted
      || !sessionToken
    ) {
      return;
    }

    /** 今回の要求ID */
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setStatus(TERMS_RECONSENT_STATUS.SUBMITTING);
    setErrorMessage('');

    try {
      await visitorAcceptActiveTerms(
        sessionToken,
        terms.version,
        true,
      );

      if (requestIdRef.current !== requestId) {
        return;
      }

      setIsTermsAccepted(false);
      setStatus(TERMS_RECONSENT_STATUS.SATISFIED);
    } catch (error) {
      logError('利用規約への再同意を記録できませんでした。', {
        errorCode: error?.code,
        errorMessage: error instanceof Error ? error.message : String(error),
      });

      if (requestIdRef.current !== requestId) {
        return;
      }

      if (error?.code === VISITOR_TERMS_RECONSENT_VERSION_OUTDATED_CODE) {
        await loadConsentStatus();
        return;
      }

      setStatus(TERMS_RECONSENT_STATUS.REQUIRED);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : '利用規約への同意を記録できませんでした。',
      );
    }
  }, [
    isTermsAccepted,
    loadConsentStatus,
    sessionToken,
    status,
    terms,
  ]);

  return {
    terms,
    isBlocking: Boolean(sessionToken) && status !== TERMS_RECONSENT_STATUS.SATISFIED,
    isChecking: status === TERMS_RECONSENT_STATUS.CHECKING,
    isSubmitting: status === TERMS_RECONSENT_STATUS.SUBMITTING,
    isTermsAccepted,
    errorMessage,
    hasLoadError: status === TERMS_RECONSENT_STATUS.ERROR,
    handleToggleTermsAccepted,
    handleAcceptTerms,
    handleRetryConsentStatus: loadConsentStatus,
  };
};

export default useVisitorTermsReconsent;
